/**
 * Hook : notifications pour la cloche Sidebar.
 *
 * - Polling du compteur (count) toutes les 30 s pour le badge.
 * - La liste complète n'est chargée qu'à la première ouverture du dropdown
 *   ou via refresh() explicite.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { notificationsApi } from "../services/api";
import useAuth from "./useAuth";

const POLL_MS = 30_000;

export function useNotifications() {
  const { isAdmin, isAuthenticated, usingMock } = useAuth();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const enabled = isAuthenticated && isAdmin && !usingMock;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const refreshCount = useCallback(async () => {
    if (!enabledRef.current) return;
    try {
      const { data } = await notificationsApi.count();
      setCount(data?.unread || 0);
    } catch { /* silencieux */ }
  }, []);

  const refresh = useCallback(async () => {
    if (!enabledRef.current) return;
    setLoading(true);
    try {
      const [c, l] = await Promise.all([
        notificationsApi.count(),
        notificationsApi.list({ limit: 20 }),
      ]);
      setCount(c.data?.unread || 0);
      // Tri : non lues d'abord, puis par date desc
      const list = (l.data || []).slice().sort((a, b) => {
        if (a.lue !== b.lue) return a.lue ? 1 : -1;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });
      setItems(list);
    } catch { /* silencieux */ }
    finally { setLoading(false); }
  }, []);

  // Premier fetch + polling du compteur
  useEffect(() => {
    if (!enabled) {
      setCount(0); setItems([]);
      return;
    }
    refreshCount();
    const id = setInterval(refreshCount, POLL_MS);
    return () => clearInterval(id);
  }, [enabled, refreshCount]);

  return { count, items, loading, refresh, refreshCount };
}

export default useNotifications;
